import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of, timer } from 'rxjs';
import { catchError, switchMap, finalize, filter, take } from 'rxjs/operators';
import { LocalStorageService } from '../providers/local-storage/local-storage.service';
import { AuthenticationService } from '../providers/auth/auth.service';
import { LoginApiResponse } from '../providers/auth/auth.models';
import { ApiError } from '../providers/error-handling/error.models';
import { MessageBusService } from 'src/app/core/providers/busMessage/messageBusService';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '../providers/toast/toast.service';

@Injectable({
  providedIn: 'root',
})
export class ConnexionInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private canRefresh = true;
  private refreshCooldown = 15000;

  constructor(
    private localStorageService: LocalStorageService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private busMessage: MessageBusService,
    private authenticationService: AuthenticationService<
      LoginApiResponse | ApiError
    >
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const headersConfig = {
      Accept: 'application/ld+json',
    };

    let request = req.clone({ setHeaders: headersConfig });
    const r = next.handle(request);

    return r.pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 401 &&
          error.error.message === 'Expired JWT Token'
        ) {
          return this.handle401Error(request, next);
        }
        return throwError(error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing && this.canRefresh) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      this.canRefresh = false; // Empêche les tentatives de rafraîchissement pendant le délai
      timer(this.refreshCooldown).subscribe(() => (this.canRefresh = true)); // Réactive les tentatives après le délai

      return this.authenticationService.refreshToken().pipe(
        switchMap((response: any) => {
          if (response.isError) {
            return throwError(() => new Error(response.message));
          }
          this.isRefreshing = false;
          this.localStorageService.setItem('authToken', response.token);
          this.localStorageService.setItem(
            'refreshToken',
            response.refresh_token
          );
          this.refreshTokenSubject.next(response.token);
          this.toastService.show({
            message: 'Vous avez été reconnecté avec succès.',
            classname: 'bg-success text-light',
            delay: 5000,
          });

          return next.handle(this.addTokenHeader(request, response.token));
        }),
        catchError((error: HttpErrorResponse) => {
          this.isRefreshing = false;
          this.localStorageService.clear();
          this.busMessage.setMessage({ type: 'LOGIN_FORM' });
          return throwError(() => error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => next.handle(this.addTokenHeader(request, token)))
      );
    }
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
